/**
 * Subscriptions page — reachable via /abonnementen.
 * Shows the pricing tiers (same content as the marketing homepage) with a
 * discount-code input. Selecting a tier navigates to /register with plan,
 * billing cycle and (optionally) the applied discount code in the URL.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback tiers for when the CMS content is not yet loaded / empty.
const FALLBACK_PRICING = {
  eyebrow: "Abonnementen",
  headline: "Kies je plan",
  subtitle: "Later betaal je per team; nu tijdens de MVP gratis met een kortingscode.",
  toggle_monthly: "Maandelijks",
  toggle_yearly: "Jaarlijks",
  tiers: [
    {
      name: "Basis",
      price_monthly: "€ 4",
      price_yearly: "€ 40",
      sub_monthly: "per team / maand",
      sub_yearly: "per team / jaar",
      features: ["1 team", "Basis planning", "KNHB import"],
    },
    {
      name: "Standaard",
      badge: "Populair",
      highlight: true,
      price_monthly: "€ 8",
      price_yearly: "€ 80",
      sub_monthly: "per team / maand",
      sub_yearly: "per team / jaar",
      features: ["1 team", "AI planning", "Teamuitgaven", "Wedstrijdkaarten"],
    },
    {
      name: "Pro",
      price_monthly: "€ 14",
      price_yearly: "€ 140",
      sub_monthly: "per team / maand",
      sub_yearly: "per team / jaar",
      features: ["Alles uit Standaard", "Meerdere teams", "Prioriteitssupport"],
    },
  ],
};

const planSlug = (name) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCode = (searchParams.get("code") || "").toUpperCase();
  const [pricing, setPricing] = useState(FALLBACK_PRICING);
  const [yearly, setYearly] = useState(false);
  const [code, setCode] = useState(urlCode);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null); // {code, discount_percent}

  useEffect(() => {
    axios
      .get(`${API}/homepage`)
      .then((res) => {
        const p = res.data?.content?.pricing;
        if (p && (p.tiers || []).length > 0) setPricing(p);
      })
      .catch(() => {});
  }, []);

  // Auto-apply code from URL on mount
  useEffect(() => {
    if (!urlCode) return;
    (async () => {
      setValidating(true);
      try {
        const res = await axios.get(`${API}/discount-codes/validate/${encodeURIComponent(urlCode)}`);
        setValidated({ code: res.data.code, discount_percent: res.data.discount_percent });
      } catch {
        // silent — user can still enter manually
      } finally {
        setValidating(false);
      }
    })();
  }, [urlCode]);

  const applyCode = async () => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;
    setValidating(true);
    try {
      const res = await axios.get(`${API}/discount-codes/validate/${encodeURIComponent(trimmed)}`);
      setValidated({ code: res.data.code, discount_percent: res.data.discount_percent });
      toast.success(`Kortingscode ${res.data.code} toegepast: ${res.data.discount_percent}% korting`);
    } catch (err) {
      setValidated(null);
      toast.error(err.response?.data?.detail || "Kortingscode ongeldig");
    } finally {
      setValidating(false);
    }
  };

  const clearCode = () => {
    setCode("");
    setValidated(null);
  };

  const goToRegister = (tier) => {
    const params = new URLSearchParams();
    params.set("plan", planSlug(tier.name));
    params.set("cycle", yearly ? "yearly" : "monthly");
    if (validated?.code) params.set("code", validated.code);
    navigate(`/register?${params.toString()}`);
  };

  const tiers = pricing.tiers || [];

  return (
    <div
      data-testid="subscriptions-page"
      style={{
        minHeight: "100vh",
        background: "var(--chalk-50, #F7F5EE)",
        fontFamily: "var(--sportly-font-body, system-ui)",
      }}
    >
      {/* Simple top nav */}
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "var(--chalk-50, #F7F5EE)",
          borderBottom: "1px solid var(--mist-200, #E5E1D6)",
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" data-testid="subscriptions-logo-link" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit" }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: "var(--pitch-600, #0E7C3A)",
                color: "#F7F5EE",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--sportly-font-display, serif)",
                fontWeight: 900,
                fontSize: 22,
              }}
            >
              S
            </div>
            <span
              style={{
                fontFamily: "var(--sportly-font-display, serif)",
                fontWeight: 900,
                fontSize: 22,
                textTransform: "uppercase",
              }}
            >
              Sportly
            </span>
          </Link>
          <div style={{ display: "flex", gap: 12 }}>
            <Link to="/login" data-testid="subscriptions-login-link">
              <Button variant="outline" size="sm">Inloggen</Button>
            </Link>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 20px 80px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ color: "var(--strike-500, #FF6B35)", fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
            {pricing.eyebrow || "Abonnementen"}
          </div>
          <h1
            className="display"
            style={{
              fontFamily: "var(--sportly-font-display, serif)",
              fontWeight: 900,
              fontSize: "clamp(36px, 5vw, 56px)",
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            {pricing.headline || "Kies je plan"}
          </h1>
          {pricing.subtitle && (
            <p style={{ marginTop: 12, color: "var(--ink-700, #3A423F)", fontSize: 16 }}>
              {pricing.subtitle}
            </p>
          )}
        </div>

        {/* Discount code input */}
        <div
          style={{
            background: "#fff",
            border: "1px solid var(--mist-200, #E5E1D6)",
            borderRadius: 16,
            padding: 20,
            maxWidth: 520,
            margin: "0 auto 36px",
          }}
        >
          <Label htmlFor="discount-input" className="text-sm font-medium mb-1 block">
            Kortingscode <span className="text-slate-400 font-normal">(optioneel)</span>
          </Label>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              id="discount-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Bijv. TESTGROEP2026"
              disabled={!!validated}
              data-testid="discount-code-input"
            />
            {validated ? (
              <Button type="button" variant="outline" onClick={clearCode} data-testid="discount-code-clear-btn">
                Wissen
              </Button>
            ) : (
              <Button
                type="button"
                onClick={applyCode}
                disabled={validating || !code.trim()}
                data-testid="discount-code-apply-btn"
              >
                {validating ? "..." : "Toepassen"}
              </Button>
            )}
          </div>
          {validated && (
            <div
              data-testid="discount-code-applied"
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 8,
                background: "#ECFDF5",
                color: "#065F46",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Sparkles className="w-4 h-4" />
              Code <strong>{validated.code}</strong> toegepast — {validated.discount_percent}% korting
            </div>
          )}
        </div>

        {/* Billing toggle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <div style={{ display: "inline-flex", background: "#fff", border: "1px solid var(--mist-200, #E5E1D6)", padding: 4, borderRadius: 999 }}>
            {[
              { k: false, l: pricing.toggle_monthly || "Maandelijks" },
              { k: true, l: pricing.toggle_yearly || "Jaarlijks" },
            ].map((opt) => (
              <button
                key={String(opt.k)}
                type="button"
                onClick={() => setYearly(opt.k)}
                data-testid={`subscriptions-toggle-${opt.k ? "yearly" : "monthly"}`}
                style={{
                  border: 0,
                  padding: "10px 18px",
                  borderRadius: 999,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  background: yearly === opt.k ? "var(--pitch-600, #0E7C3A)" : "transparent",
                  color: yearly === opt.k ? "#fff" : "var(--ink-700, #3A423F)",
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing grid */}
        <div
          className="pricing-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {tiers.map((t, i) => {
            const tierKey = planSlug(t.name) || `tier-${i}`;
            const price = yearly ? t.price_yearly : t.price_monthly;
            const sub = yearly ? t.sub_yearly : t.sub_monthly;
            const discounted = validated && validated.discount_percent === 100 ? "GRATIS" : null;
            return (
              <div
                key={tierKey}
                style={{
                  background: t.highlight ? "var(--ink-900, #0B1410)" : "#fff",
                  color: t.highlight ? "#fff" : "var(--ink-900, #0B1410)",
                  border: t.highlight ? "none" : "1px solid var(--mist-200, #E5E1D6)",
                  borderRadius: 20,
                  padding: "28px 24px",
                  position: "relative",
                  boxShadow: t.highlight ? "0 20px 60px rgba(11,20,16,0.18)" : "none",
                  display: "flex",
                  flexDirection: "column",
                }}
                data-testid={`subscriptions-tier-${planSlug(t.name)}`}
              >
                {t.badge && (
                  <span
                    style={{
                      position: "absolute",
                      top: -14,
                      left: 24,
                      background: "var(--strike-500, #FF6B35)",
                      color: "#fff",
                      padding: "5px 12px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {t.badge}
                  </span>
                )}
                <div className="display" style={{ fontFamily: "var(--sportly-font-display, serif)", fontSize: 28, marginBottom: 6, fontWeight: 900 }}>
                  {t.name}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, minHeight: 56 }}>
                  {discounted ? (
                    <>
                      <span
                        style={{
                          fontFamily: "var(--sportly-font-display, serif)",
                          fontWeight: 900,
                          fontSize: 44,
                          lineHeight: 1,
                          color: t.highlight ? "#D4FF3E" : "var(--pitch-600, #0E7C3A)",
                        }}
                      >
                        {discounted}
                      </span>
                      <span style={{ textDecoration: "line-through", opacity: 0.55, fontSize: 16 }}>{price}</span>
                    </>
                  ) : (
                    <span
                      style={{
                        fontFamily: "var(--sportly-font-display, serif)",
                        fontWeight: 900,
                        fontSize: (price || "").length > 5 ? 36 : 52,
                        lineHeight: 1,
                      }}
                    >
                      {price}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: t.highlight ? "rgba(255,255,255,0.7)" : "var(--ink-500, #5B6461)", marginBottom: 22 }}>
                  {sub}
                </div>
                <Button
                  onClick={() => goToRegister(t)}
                  className={t.highlight ? "" : "bg-slate-900 hover:bg-slate-800"}
                  style={{
                    width: "100%",
                    marginBottom: 20,
                    ...(t.highlight ? { background: "var(--strike-500, #FF6B35)", color: "#fff" } : {}),
                  }}
                  data-testid={`subscriptions-select-${planSlug(t.name)}`}
                >
                  Kies {t.name}
                </Button>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {(t.features || []).map((f, j) => (
                    <li key={`${tierKey}-feat-${j}`} style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 10, color: t.highlight ? "rgba(255,255,255,0.9)" : "var(--ink-700, #3A423F)" }}>
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: t.highlight ? "#D4FF3E" : "#0E7C3A" }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p style={{ marginTop: 32, textAlign: "center", fontSize: 13, color: "var(--ink-500, #5B6461)" }}>
          Al een account? <Link to="/login" style={{ textDecoration: "underline" }}>Inloggen</Link>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
